/**
 * Central media configuration for audio, video, LiveKit Room options, and intervals.
 * Single source of truth for channels, tracks, noise cancellation, video quality, and timing.
 * Extend by adding presets, quality tiers, or Room options without changing consumers.
 */

import {
  getLiveKitAudioDefaults,
  getAudioConstraints,
  type AudioProcessingConfig,
  type AudioConstraintOptions,
} from '../utils/audioProcessingConfig'
import {
  standardAudioConfig,
  enhancedNoiseSuppressionConfig,
  audioPresets,
} from '../utils/audioProcessingConfig'
import type { AudioPreset } from '../utils/audioProcessingConfig'
import { VideoPreset, type ScreenShareCaptureOptions, type TrackPublishDefaults } from 'livekit-client'

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

export interface VideoConstraints {
  width: number
  height: number
  frameRate: number
}

export const VIDEO_PRESETS = {
  /** 1280x720, 30 fps – default for LiveKit capture and getUserMedia */
  h720: { width: 1280, height: 720, frameRate: 30 },
  /**
   * 1280x720, 24 fps - capture while a background effect is on (SC-478). A
   * separate entry so `h720` keeps meaning what it says for anything that picks
   * it without an effect.
   */
  h720Effect: { width: 1280, height: 720, frameRate: 24 },
  /** 854x480, 24 fps – optimized quality tier */
  h480: { width: 854, height: 480, frameRate: 24 },
  /** 640x360, 30 fps – lower bandwidth */
  h360: { width: 640, height: 360, frameRate: 30 },
  /** Same as h360 – explicit alias for getUserMedia default */
  getUserMediaDefault: { width: 640, height: 360, frameRate: 30 },
} as const satisfies Record<string, VideoConstraints>

export type VideoPresetKey = keyof typeof VIDEO_PRESETS

export const defaultVideoPreset: VideoPresetKey = 'h360'

/**
 * Capture resolution to use while a background effect is on. [SC-158]
 *
 * Segmentation quality is bounded by capture resolution, and at `h360`
 * (640×360) it is visibly bad: a finger is roughly 15px wide with ~8px gaps
 * between fingers, and `@livekit/track-processors` box-blurs the mask by a
 * **hardcoded 8px** before compositing (`maskBlurRadius`, no setter). A feather
 * as wide as the gaps closes them, which is why a raised hand came out as a
 * mitten. The model also resizes its input to 256×256, so at 360p the detail is
 * gone before segmentation even runs.
 *
 * At 720p the same fixed 8px feather is relatively half as wide and the gaps
 * survive.
 *
 * Only applied when an effect is actually on, so users without one pay nothing.
 * It deliberately overrides the connection profile's choice: that profile is
 * chosen for *network*, and `adaptiveStream` + `dynacast` mean a higher capture
 * does not push a higher bitrate to subscribers who do not need it. The cost is
 * local GPU. On a device that cannot keep up, the slow-frame monitor warns once
 * (offering "Use blur" or "Turn effect off") and leaves the choice to the user:
 * it never turns the effect off or lowers the capture, by product decision,
 * because either would show the room the user chose to hide. The monitor's
 * thresholds are derived for this preset's 24 fps (`DEFAULT_BACKGROUND_HEALTH_THRESHOLDS`).
 *
 * 24 fps rather than 30 [SC-478]: segmentation and compositing run once per
 * captured frame, so the frame rate is the one lever that cuts CPU/GPU load
 * 20% with no loss of detail; resolution has to stay at 720p for the fingers.
 * 24 fps is still film-smooth for a head-and-shoulders tile and adds no delay.
 */
export const BACKGROUND_EFFECT_VIDEO_PRESET: VideoPresetKey = 'h720Effect'

/**
 * The capture preset to use, given the network-chosen one and whether a
 * background effect is on.
 */
export function videoPresetForBackgroundEffect(
  basePreset: VideoPresetKey | undefined,
  backgroundEffectActive: boolean
): VideoPresetKey {
  if (!backgroundEffectActive) return basePreset ?? defaultVideoPreset
  return BACKGROUND_EFFECT_VIDEO_PRESET
}

/**
 * How far a camera's reported frame rate may sit from the rate asked for and still
 * count as that rate. Cameras report what the driver delivers (24.00003, or 25 on
 * a camera with no 24 fps mode), and asking again cannot change that.
 */
export const CAPTURE_FRAME_RATE_TOLERANCE_FPS = 1

/**
 * Whether a camera's live settings already are the effect capture (SC-478): at
 * least its height, and no more than its frame rate within
 * {@link CAPTURE_FRAME_RATE_TOLERANCE_FPS}. A rate that is not reported counts as
 * met, as does one below the target: the cap is what saves the work, and a
 * camera that runs slower cannot be asked for a faster one to any use.
 *
 * One answer for the pre-join preview and the call, so a pick does not send an
 * `applyConstraints` in one place that the other has just decided is pointless.
 */
export function meetsEffectCapture(
  settings: Pick<MediaTrackSettings, 'height' | 'frameRate'> | undefined
): boolean {
  const target = VIDEO_PRESETS[BACKGROUND_EFFECT_VIDEO_PRESET]
  if ((settings?.height ?? 0) < target.height) return false
  const frameRate = settings?.frameRate
  return frameRate === undefined || frameRate <= target.frameRate + CAPTURE_FRAME_RATE_TOLERANCE_FPS
}

/**
 * Video capture defaults for LiveKit Room (resolution + frameRate; deviceId applied at call site).
 * Return shape matches LiveKit VideoCaptureOptions (resolution: { width, height }, frameRate).
 */
export function getVideoCaptureDefaults(
  deviceId?: string,
  preset?: VideoPresetKey,
  overrides?: Partial<VideoConstraints>
): {
  resolution: { width: number; height: number }
  frameRate: number
  deviceId?: string
} {
  const p = VIDEO_PRESETS[preset ?? defaultVideoPreset]
  const width = overrides?.width ?? p.width
  const height = overrides?.height ?? p.height
  const frameRate = overrides?.frameRate ?? p.frameRate
  const result: {
    resolution: { width: number; height: number }
    frameRate: number
    deviceId?: string
  } = {
    resolution: { width, height },
    frameRate,
  }
  if (deviceId) result.deviceId = deviceId
  return result
}

/**
 * Video constraints for getUserMedia (ideal width/height/frameRate).
 */
export function getGetUserMediaVideoConstraints(
  deviceId?: string,
  preset?: VideoPresetKey
): MediaTrackConstraints {
  const p = VIDEO_PRESETS[preset ?? 'getUserMediaDefault']
  return {
    ...(deviceId ? { deviceId: { ideal: deviceId } } : {}),
    width: { ideal: p.width },
    height: { ideal: p.height },
    frameRate: { ideal: p.frameRate },
  }
}

// ---------------------------------------------------------------------------
// Adaptive quality (connection quality → video constraints)
// ---------------------------------------------------------------------------

export type ConnectionQualityKey = 'excellent' | 'good' | 'poor' | 'unknown'

// Participant tiles stay small in the UI, so even healthy connections should
// avoid bouncing back to HD and keep headroom for stable audio.
export const ADAPTIVE_QUALITY_TIERS: Record<ConnectionQualityKey, VideoConstraints> = {
  excellent: { width: 640, height: 360, frameRate: 30 },
  good: { width: 640, height: 360, frameRate: 25 },
  poor: { width: 640, height: 480, frameRate: 20 },
  unknown: { width: 640, height: 480, frameRate: 15 },
}

export function getAdaptiveQualityConstraints(
  quality: ConnectionQualityKey
): VideoConstraints {
  return ADAPTIVE_QUALITY_TIERS[quality] ?? ADAPTIVE_QUALITY_TIERS.unknown
}

// ---------------------------------------------------------------------------
// Virtual background / video effects (SC-158)
// ---------------------------------------------------------------------------

/** Values that switch the virtual-background feature off, compared case-insensitively. */
const VIRTUAL_BACKGROUND_DISABLED_VALUES = new Set(['false', '0', 'off', 'no'])

/**
 * Build-time rollout gate for the virtual-background feature.
 *
 * **On by default.** The feature ships enabled and
 * `VITE_FEATURE_VIRTUAL_BACKGROUND` exists to turn it *off* — a kill switch, not
 * an opt-in. Unset, empty or unrecognised values leave it on.
 *
 * The leniency is deliberate and it is the opposite of what this function used
 * to do. While the default was off, strictness was the safe direction: only the
 * exact string `'true'` enabled anything, so a typo shipped the feature
 * disabled. Now that the default is on, the *disable* path is the safety valve,
 * and a kill switch that fails because someone wrote `FALSE` or `0` is the
 * dangerous outcome. So the off-switch accepts the spellings people actually
 * use, case-insensitively and trimmed.
 *
 * What that trades away: a genuine typo (`flase`) leaves the feature on rather
 * than off, and there is no way to distinguish it from someone who never set the
 * flag. Anyone disabling this should confirm it in the built app rather than
 * assume the env var took.
 *
 * This is a per-deployment switch only — there is no per-user rollout. It is
 * independent of the runtime capability check
 * (`supportsBackgroundProcessors()`), which hides the control on devices that
 * cannot run segmentation regardless of this flag.
 *
 * Note this reads `import.meta.env`, not `process.env`: Vite only substitutes
 * the former, so a `process.env.VITE_*` read would silently always be
 * undefined. See `src/config/defaultSettings.ts` for that failure mode.
 */
export function isVirtualBackgroundEnabled(): boolean {
  const raw = import.meta.env.VITE_FEATURE_VIRTUAL_BACKGROUND
  if (typeof raw !== 'string') return true
  return !VIRTUAL_BACKGROUND_DISABLED_VALUES.has(raw.trim().toLowerCase())
}

/** Resolve a public/ asset path against Vite's configured base URL. */
export function publicAssetPath(relativePath: string): string {
  const base = import.meta.env.BASE_URL || '/'
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base
  const path = `${trimmed}/${relativePath}`.replace(/\/+/g, '/')
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * A segmentation model we ship under `public/livekit/track-processors/models/`.
 *
 * The file and how its output becomes the mask live in one entry on purpose.
 * The compositor reads mask > 0.5 as "background", and what each model's output
 * means is a property of the file, not of the API:
 *
 *  - the selfie model's *category mask* already is that (background 255, person
 *    0), so it needs nothing (`backgroundClass` unset);
 *  - the multiclass model's category mask is the raw class index (background 0),
 *    which the compositor would read as "person" in every pixel and show the
 *    whole room. Its *confidence mask for the background class* has the right
 *    direction and is soft at the edge, so `backgroundClass` names it.
 *
 * A path swapped without its `backgroundClass` fails in the one way nothing else
 * catches: the effect is on and the room is visible. Keeping the two together
 * means a model cannot be switched without its mask rule.
 */
export interface VirtualBackgroundModel {
  /** File name inside `models/`. */
  file: string
  /**
   * Index of the class whose confidence mask is the background. Left out, the
   * model's category mask is used. The patched library checks the model's own
   * label for this class says "background" and refuses to start otherwise.
   */
  backgroundClass?: number
}

/**
 * The models we ship. Both stay in the image: the second is what a device that
 * cannot keep up with the first steps down to.
 *
 * Provenance: both files are committed under `public/` and pinned by SHA-256 in
 * `syncTrackProcessorAssetsScript.test.ts`; upstream only publishes them behind
 * a mutable `latest` URL.
 */
export const VIRTUAL_BACKGROUND_MODELS = {
  /** MediaPipe "Selfie multiclass, 256x256": 0 background, 1 hair, 2 body-skin, 3 face-skin, 4 clothes, 5 others. */
  selfie_multiclass: { file: 'selfie_multiclass_256x256.tflite', backgroundClass: 0 },
  /** MediaPipe selfie segmenter: the model shipped before the multiclass one. */
  selfie_segmenter: { file: 'selfie_segmenter.tflite' },
} as const satisfies Record<string, VirtualBackgroundModel>

export type VirtualBackgroundModelId = keyof typeof VIRTUAL_BACKGROUND_MODELS

/** The model segmentation starts with. Going back to the previous model is this one line. */
export const DEFAULT_VIRTUAL_BACKGROUND_MODEL: VirtualBackgroundModelId = 'selfie_multiclass'

/**
 * The model a device steps down to once, when the default is too slow for it.
 * With the default set to this one there is nothing to step down to.
 */
export const FALLBACK_VIRTUAL_BACKGROUND_MODEL: VirtualBackgroundModelId = 'selfie_segmenter'

export interface VirtualBackgroundAssetPaths {
  /** Directory holding the MediaPipe vision WASM fileset. */
  tasksVisionFileSet: string
  /** The segmentation TFLite model. */
  modelAssetPath: string
  /**
   * Read by our patch of `@livekit/track-processors`: the class whose
   * confidence mask is the background. Absent, the library uses the category
   * mask. See {@link VirtualBackgroundModel}.
   */
  modelBackgroundClass?: number
}

/**
 * Self-hosted MediaPipe asset locations.
 *
 * Left unset, `@livekit/track-processors` fetches the WASM fileset from
 * jsDelivr and the model from `storage.googleapis.com` — third-party runtime
 * dependencies on the call path, and the model URL is a mutable `latest`
 * pointer. We serve both from our own origin instead.
 *
 * These are read once at processor construction and cannot be changed
 * afterwards without restarting the processor, so they must be passed on the
 * first `BackgroundProcessor(...)` call. That is also why changing the model
 * means building another processor.
 *
 * Provenance: the WASM directory is copied out of node_modules by
 * `scripts/sync-track-processor-assets.cjs` (postinstall + prebuild) and is
 * gitignored; the models are committed, which is what pins them.
 */
export function getVirtualBackgroundAssetPaths(
  modelId: VirtualBackgroundModelId = DEFAULT_VIRTUAL_BACKGROUND_MODEL
): VirtualBackgroundAssetPaths {
  const model: VirtualBackgroundModel = VIRTUAL_BACKGROUND_MODELS[modelId]
  return {
    tasksVisionFileSet: publicAssetPath('livekit/track-processors/wasm'),
    modelAssetPath: publicAssetPath(`livekit/track-processors/models/${model.file}`),
    ...(model.backgroundClass !== undefined ? { modelBackgroundClass: model.backgroundClass } : {}),
  }
}

export type BackgroundBlurIntensity = 'light' | 'strong'

/**
 * Blur radii in pixels, passed straight to the processor's `blurRadius`.
 * Two levels only, matching the product decision in the SC-158 plan.
 */
export const BACKGROUND_BLUR_RADII: Record<BackgroundBlurIntensity, number> = {
  light: 6,
  strong: 12,
}

// ---------------------------------------------------------------------------
// Screen-share camera reduction
// ---------------------------------------------------------------------------

export interface ScreenShareReduction {
  scale: number
  minWidth: number
  minHeight: number
  frameRateFactor: number
}

export const SCREEN_SHARE_REDUCTION: ScreenShareReduction = {
  scale: 0.5,
  minWidth: 320,
  minHeight: 180,
  frameRateFactor: 0.5,
}

export interface CurrentVideoSettings {
  width?: number
  height?: number
  frameRate?: number
}

/**
 * Compute reduced video constraints when screen share is active (to save bandwidth).
 */
export function getScreenShareCameraReduction(
  currentSettings: CurrentVideoSettings
): VideoConstraints {
  const { scale, minWidth, minHeight, frameRateFactor } = SCREEN_SHARE_REDUCTION
  const w = currentSettings.width ?? 640
  const h = currentSettings.height ?? 360
  const fr = currentSettings.frameRate ?? 30
  return {
    width: Math.max(minWidth, Math.floor(w * scale)),
    height: Math.max(minHeight, Math.floor(h * scale)),
    frameRate: Math.max(15, Math.floor(fr * frameRateFactor)),
  }
}

// ---------------------------------------------------------------------------
// Screen-share capture
// ---------------------------------------------------------------------------

/**
 * Options for every `setScreenShareEnabled(true)` call, so the start toggle and
 * the reconnect restore offer the same picker.
 *
 * Requesting audio makes Chromium show "Also share tab audio" for tabs. Firefox
 * and Safari ignore it and capture video only, so it is safe everywhere.
 *
 * - Voice processing is off: the source is music or a video soundtrack, and
 *   echo cancellation / noise suppression / AGC would pump and duck it.
 * - Tab audio only. System audio (whole screen on Windows/ChromeOS) and our own
 *   Sevv tab are excluded: both carry the remote participants we are playing,
 *   which would loop everyone's voice back to them.
 * - The sharer keeps hearing their tab locally — Chromium mutes it otherwise.
 */
export const SCREEN_SHARE_CAPTURE_OPTIONS: ScreenShareCaptureOptions = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  },
  systemAudio: 'exclude',
  selfBrowserSurface: 'exclude',
  suppressLocalAudioPlayback: false,
}

// ---------------------------------------------------------------------------
// Room / connection options
// ---------------------------------------------------------------------------

export interface RoomOptions {
  adaptiveStream: boolean
  dynacast: boolean
  disconnectOnPageLeave: boolean
  stopLocalTrackOnUnpublish: boolean
  webAudioMix: boolean
  publishDefaults: TrackPublishDefaults
}

export const ROOM_OPTIONS: RoomOptions = {
  adaptiveStream: true,
  dynacast: true,
  disconnectOnPageLeave: false,
  stopLocalTrackOnUnpublish: true,
  webAudioMix: true,
  // Camera tiles need less bandwidth; screen shares keep the SDK's separate encoding.
  publishDefaults: {
    videoEncoding: { maxBitrate: 350_000, maxFramerate: 15 },
    videoSimulcastLayers: [
      new VideoPreset(320, 180, 120_000, 15),
      new VideoPreset(640, 360, 250_000, 15),
    ],
  },
}

export function getRoomOptions(overrides?: Partial<RoomOptions>): RoomOptions {
  return { ...ROOM_OPTIONS, ...overrides }
}

export interface ConnectOptions {
  autoSubscribe: boolean
  /** Retry initial join when server is unreachable (LiveKit RoomConnectOptions) */
  maxRetries?: number
  /** Time for PeerConnection to be established, ms (default 15s; 20s for slow networks) */
  peerConnectionTimeout?: number
  /** Time for WebSocket connection, ms (default 15s; 20s for slow networks) */
  websocketTimeout?: number
}

export const DEFAULT_CONNECT_OPTIONS: ConnectOptions = {
  autoSubscribe: false,
  maxRetries: 4,
  peerConnectionTimeout: 20000,
  websocketTimeout: 20000,
}

export function getConnectOptions(overrides?: Partial<ConnectOptions>): ConnectOptions {
  return { ...DEFAULT_CONNECT_OPTIONS, ...overrides }
}

// ---------------------------------------------------------------------------
// Large conference / talk-room media tiers
// ---------------------------------------------------------------------------

export type ConferenceMediaTier = 'normal' | 'conference' | 'large'

/** Tier from VR occupant count (server roster); office headcount reserved for future use. */
export function getConferenceMediaTier(
  vrOccupantCount: number,
  _officePlayerCount?: number
): ConferenceMediaTier {
  if (vrOccupantCount >= 100) return 'large'
  if (vrOccupantCount >= 25) return 'conference'
  return 'normal'
}

export function getSubscriptionDebounceMs(tier: ConferenceMediaTier): number {
  switch (tier) {
    case 'large':
      return 500
    case 'conference':
      return 400
    default:
      return 300
  }
}

/** Mic auto-mute suggestion threshold inside a VR talk (from server roster count). */
export function getConferenceAutoMuteThreshold(tier: ConferenceMediaTier): number {
  switch (tier) {
    case 'large':
    case 'conference':
      return 25
    default:
      return 8
  }
}

export function shouldUseLowCameraQuality(tier: ConferenceMediaTier): boolean {
  return tier === 'conference' || tier === 'large'
}

// ---------------------------------------------------------------------------
// LiveKit reconnection UX (tiered banner vs modal; tuned for long-haul latency)
// ---------------------------------------------------------------------------

export interface LivekitReconnectUxConfig {
  /** If SDK stays in Reconnecting longer than this, force disconnect + modal path */
  reconnectingWatchdogMs: number
  maxAutoReconnectAttempts: number
  baseReconnectDelayMs: number
  /** Delay after disconnect before scheduling automatic LiveKit reconnect */
  disconnectBeforeAutoRetryDelayMs: number
  /** Cooldown after max attempts before user can retry from dialog */
  liveKitCooldownMs: number
  /** Suppress repeat blocking modals within this window (unless forceModal) */
  modalMinIntervalMs: number
  /** 1-based attempt index: show modal when attempt >= this (earlier = nameplate/toast only) */
  modalAfterAttempt: number
  /** First delayed check for cam/mic after Reconnected */
  postReconnectTrackVerifyDelayMs: number
  /** Number of spaced verification passes before concluding tracks are missing */
  postReconnectTrackVerifyRetries: number
  /** Extra ms between post-reconnect verification passes */
  postReconnectTrackVerifyIntervalMs: number
  /** Second retryable LIVEKIT_ERROR within this window → escalate to modal */
  retryableErrorEscalateWindowMs: number
}

const defaultConnect = DEFAULT_CONNECT_OPTIONS
const peerMs = defaultConnect.peerConnectionTimeout ?? 20000
const wsMs = defaultConnect.websocketTimeout ?? 20000

/** Defaults favor patience for high-RTT / long-haul users */
export const LIVEKIT_RECONNECT_UX: LivekitReconnectUxConfig = {
  reconnectingWatchdogMs: Math.max(55000, peerMs + wsMs),
  maxAutoReconnectAttempts: 5,
  baseReconnectDelayMs: 1000,
  disconnectBeforeAutoRetryDelayMs: 3500,
  liveKitCooldownMs: 30000,
  modalMinIntervalMs: 12000,
  modalAfterAttempt: 4,
  postReconnectTrackVerifyDelayMs: 4500,
  postReconnectTrackVerifyRetries: 4,
  postReconnectTrackVerifyIntervalMs: 2000,
  retryableErrorEscalateWindowMs: 45000,
}

function parseEnvInt(value: string | undefined, fallback: number): number {
  if (value == null || value === '') return fallback
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/**
 * LiveKit reconnect UX with optional Vite env overrides (all optional).
 * Example: VITE_LIVEKIT_RECONNECT_WATCHDOG_MS=60000
 */
export function getLivekitReconnectUxConfig(
  overrides?: Partial<LivekitReconnectUxConfig>
): LivekitReconnectUxConfig {
  const env = import.meta.env
  const base = { ...LIVEKIT_RECONNECT_UX, ...overrides }
  return {
    reconnectingWatchdogMs: parseEnvInt(
      env.VITE_LIVEKIT_RECONNECT_WATCHDOG_MS as string | undefined,
      base.reconnectingWatchdogMs
    ),
    maxAutoReconnectAttempts: parseEnvInt(
      env.VITE_LIVEKIT_MAX_AUTO_RECONNECT_ATTEMPTS as string | undefined,
      base.maxAutoReconnectAttempts
    ),
    baseReconnectDelayMs: parseEnvInt(
      env.VITE_LIVEKIT_BASE_RECONNECT_DELAY_MS as string | undefined,
      base.baseReconnectDelayMs
    ),
    disconnectBeforeAutoRetryDelayMs: parseEnvInt(
      env.VITE_LIVEKIT_DISCONNECT_BEFORE_RETRY_MS as string | undefined,
      base.disconnectBeforeAutoRetryDelayMs
    ),
    liveKitCooldownMs: parseEnvInt(
      env.VITE_LIVEKIT_COOLDOWN_MS as string | undefined,
      base.liveKitCooldownMs
    ),
    modalMinIntervalMs: parseEnvInt(
      env.VITE_LIVEKIT_MODAL_MIN_INTERVAL_MS as string | undefined,
      base.modalMinIntervalMs
    ),
    modalAfterAttempt: parseEnvInt(
      env.VITE_LIVEKIT_MODAL_AFTER_ATTEMPT as string | undefined,
      base.modalAfterAttempt
    ),
    postReconnectTrackVerifyDelayMs: parseEnvInt(
      env.VITE_LIVEKIT_POST_RECONNECT_VERIFY_DELAY_MS as string | undefined,
      base.postReconnectTrackVerifyDelayMs
    ),
    postReconnectTrackVerifyRetries: parseEnvInt(
      env.VITE_LIVEKIT_POST_RECONNECT_VERIFY_RETRIES as string | undefined,
      base.postReconnectTrackVerifyRetries
    ),
    postReconnectTrackVerifyIntervalMs: parseEnvInt(
      env.VITE_LIVEKIT_POST_RECONNECT_VERIFY_INTERVAL_MS as string | undefined,
      base.postReconnectTrackVerifyIntervalMs
    ),
    retryableErrorEscalateWindowMs: parseEnvInt(
      env.VITE_LIVEKIT_RETRYABLE_ERROR_ESCALATE_MS as string | undefined,
      base.retryableErrorEscalateWindowMs
    ),
  }
}

// ---------------------------------------------------------------------------
// Intervals
// ---------------------------------------------------------------------------

export interface MediaIntervals {
  bandwidthCheckMs: number
  qualityCheckMs: number
}

export const MEDIA_INTERVALS: MediaIntervals = {
  bandwidthCheckMs: 10000,
  qualityCheckMs: 5000,
}

// ---------------------------------------------------------------------------
// Audio (delegate to audioProcessingConfig; single default preset)
// ---------------------------------------------------------------------------

export const defaultAudioPreset: AudioProcessingConfig = standardAudioConfig

export function getDefaultLiveKitAudioDefaults(
  deviceId?: string,
  options?: AudioConstraintOptions
): ReturnType<typeof getLiveKitAudioDefaults> {
  return getLiveKitAudioDefaults(deviceId, defaultAudioPreset, options)
}

export function getDefaultAudioConstraints(
  deviceId?: string,
  options?: AudioConstraintOptions
): MediaStreamConstraints['audio'] {
  return getAudioConstraints(deviceId, defaultAudioPreset, options)
}

export function getAudioConstraintsForPreset(
  deviceId: string | undefined,
  preset: AudioProcessingConfig,
  options?: AudioConstraintOptions
): MediaStreamConstraints['audio'] {
  return getAudioConstraints(deviceId, preset, options)
}

/** Preset for consumers that need enhanced noise suppression (e.g. getUserMedia in useMediaDevices). */
export function getEnhancedAudioPreset(): AudioProcessingConfig {
  return enhancedNoiseSuppressionConfig
}

/** All audio presets from audioProcessingConfig for advanced use. */
export { audioPresets }
export type { AudioPreset }
